import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  RotateCcw,
  Calendar,
  Search,
  Users,
  MoreHorizontal,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ProgramTableSkeleton } from "@/components/ui/program-table-skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import { ClientForm } from "@/components/clients/ClientForm";
import { ProgramApi } from "@/api";
import { getFormattedDate, getFormattedDateTime } from "@/utils";
import { LoaderButton } from "@/components/ui/loader-button";
import {
  addDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
} from "date-fns";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { PAGE_SIZE } from "@/constants/common.constant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
// Import permission components and constants
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES, FEATURE_CODES } from "@/constants/permissions";

// Moved filters inside component to avoid reference issues

// Helper to get start and end in millis for a given range
const getDateRangeMillis = (range) => {
  const now = new Date();
  let start, end;
  switch (range) {
    case "30days":
      start = startOfDay(addDays(now, -29));
      end = endOfDay(now);
      break;
    case "7days":
      start = startOfDay(addDays(now, -6));
      end = endOfDay(now);
      break;
    case "90days":
      start = startOfDay(addDays(now, -89));
      end = endOfDay(now);
      break;
    case "all":
      start = null;
      end = null;
      break;
    default:
      start = null;
      end = null;
  }
  return {
    startDateInMillis: range === "all" ? null : start ? start.getTime() : null,
    endDateInMillis: range === "all" ? null : end ? end.getTime() : null,
  };
};

const DELAY = 500;

const Programs = ({ basePath = "/accesspoint" }: { basePath?: string }) => {
  const apiBaseUrl = (
    import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL || ""
  ).trim();
  const primaryBtnClass =
    basePath === "/offers"
      ? "bg-rose-600 hover:bg-rose-700"
      : "bg-blue-600 hover:bg-blue-700";
  const [searchType, setSearchType] = useState("programName"); // 'programName' or 'programCode'
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [filteredPrograms, setFilteredPrograms] = useState<any[]>([]);
  const [clientSheetOpen, setClientSheetOpen] = useState(false);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.ceil(totalPrograms / rowsPerPage);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [programCodeQuery, setProgramCodeQuery] = useState("");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Define filters inside component to avoid reference issues
  const filters = useMemo(
    () => ({
      pageSize: PAGE_SIZE,
      programCode: null,
      programName: null,
      programType: "EARN",
      sortBy: "createdDate",
      sortOrder: "desc",
    }),
    [],
  );

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number,
  ) {
    return function (...args: Parameters<T>) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        func(...args);
        debounceTimeout.current = null;
      }, delay);
    };
  }

  const fetchPrograms = useCallback(
    async (customFilters = {}) => {
      try {
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        setIsLoading(true);

        let data;
        if (basePath === "/offers") {
          const res = await ProgramApi.fetchOfferProgram({
            pageNumber: 0,
            pageSize: PAGE_SIZE,
            sortOrder: "desc",
            sortBy: "createdDate",
            ...customFilters,
          });
          data = res?.data;
          setTotalPrograms(
            data?.totalNumberOfPrograms ||
              data?.totalNumberOfOfferPrograms ||
              0,
          );
        } else {
          // Get date range millis for API call
          const dateRangeData = getDateRangeMillis(dateRange);

          // Include current search parameters with trimmed values
          const searchParams = searchQuery
            ? { [searchType]: searchQuery.trim() }
            : {};

          const res = await ProgramApi.fetchProgram({
            ...filters,
            ...customFilters,
            ...searchParams,
            startDateInMillis: dateRangeData.startDateInMillis,
            endDateInMillis: dateRangeData.endDateInMillis,
          });
          data = res?.data;
          setTotalPrograms(data?.totalNumberOfPrograms || 0);
        }

        const programList = data?.programVOs || data?.programOfferVOs || [];
        const programs = programList?.map((program) => ({
          id: program?.id,
          name: program?.programName,
          address: program?.address || "-",
          pointsName: program?.loyaltyCurrencyId || "-",
          city: program?.city || "-",
          country: program?.country || "-",
          programCode: program?.programCode,
          currency: program?.currencyVO?.iso3CurrencyCode,
          costPerPoint: program?.costPerPoint,
          merchants: 0,
          status: "active",
          created: program?.createdDateInMillis
            ? getFormattedDateTime(program?.createdDateInMillis)
            : "",
          logo: program?.logo,
          programLogoUri: program?.programLogoUri,
          invoiceFrequencyInDays: program?.invoiceFrequencyInDays,
          explorerUrl: program?.explorerUrl || null,
        }));
        setFilteredPrograms(programs || []);
      } catch (err) {
        // Don't show error if request was aborted
        if (err.name !== "AbortError") {
          toast.error("Error in fetching programs");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [filters, dateRange, basePath],
  );
  useEffect(() => {
    fetchPrograms({
      pageNumber: page,
      pageSize: rowsPerPage,
    });
  }, [page, rowsPerPage, fetchPrograms]);

  // Cleanup effect to cancel requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setSearchType("programName");
    setDateRange("all");
    setPage(0);
    fetchPrograms({ pageNumber: 0 });
  }, [fetchPrograms]);

  const handleClientAdded = useCallback(() => {
    setClientSheetOpen(false);
  }, []);

  const confirmDeleteProgram = async () => {
    if (!programToDelete?.id) return;
    try {
      setIsDeleting(true);
      await ProgramApi.deleteProgram(programToDelete.id);
      toast.success(
        `Program ${programToDelete.name || ""} deleted successfully`,
      );
      setDeleteDialogOpen(false);
      setProgramToDelete(null);
      fetchPrograms({
        pageNumber: page,
        pageSize: rowsPerPage,
      });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.message ||
          "Failed to delete program",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setPage(0);
      fetchPrograms({
        pageNumber: 0,
        pageSize: rowsPerPage,
        [searchType]: query.trim(),
      });
    }, DELAY),
    [fetchPrograms, rowsPerPage, searchType],
  );

  const commonProgramColumns = [
    {
      header: "Created Date",
      accessorKey: "created",
    },
    {
      header: "Logo",
      accessorKey: "programLogoUri",
      cell: (row) => (
        <div className="flex items-center">
          <img
            src={
              row.programLogoUri
                ? `${apiBaseUrl}${row.programLogoUri}`
                : "/placeholder.svg"
            }
            alt={row.name}
            className="h-10 w-10 object-contain rounded-md"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },
    {
      header: "Code",
      accessorKey: "programCode",
    },
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Address",
      accessorKey: "address",
      cell: (row) => row.address || "-",
    },
  ];

  const accessPointExtraColumns = [
    {
      header: "Currency",
      accessorKey: "currency",
    },
    {
      header: "CPP",
      accessorKey: "costPerPoint",
      cell: (row) => row.costPerPoint ?? "-",
    },
    {
      header: "Invoice Frequency",
      accessorKey: "invoiceFrequencyInDays",
      cell: (row) =>
        row.invoiceFrequencyInDays ? `${row.invoiceFrequencyInDays} days` : "-",
    },
    {
      header: "City/Country",
      accessorKey: "cityCountry",
      cell: (row) => (
        <div>
          <div>{row.city}</div>
          <div className="text-xs text-gray-500 mt-0.5">{row.country}</div>
        </div>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white min-w-[8rem] p-1"
          >
            <PermissionGuard permissionCode={PERMISSION_CODES.VIEW_PROGRAM}>
              <DropdownMenuItem asChild>
                <Link to={`${basePath}/programs/${row.id}`}>View Details</Link>
              </DropdownMenuItem>
            </PermissionGuard>
            {row.explorerUrl ? (
              <DropdownMenuItem
                onClick={() =>
                  window.open(row.explorerUrl, "_blank", "noopener,noreferrer")
                }
              >
                View On Blockchain
              </DropdownMenuItem>
            ) : null}
            <PermissionGuard permissionCode={PERMISSION_CODES.DELETE_PROGRAM}>
              <DropdownMenuItem
                onClick={() => {
                  setProgramToDelete(row);
                  setDeleteDialogOpen(true);
                }}
                className="text-red-600"
              >
                Delete Program
              </DropdownMenuItem>
            </PermissionGuard>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const offersActionColumn = {
    header: "Actions",
    id: "actions",
    cell: (row) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white min-w-[8rem] p-1">
          <DropdownMenuItem asChild>
            <Link to={`${basePath}/programs/${row.id}`}>View Details</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild disabled={!row.programCode}>
            <Link
              to={`/publicoffers?programCode=${encodeURIComponent(
                row.programCode || "",
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Offers
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  const programColumns =
    basePath === "/offers"
      ? [...commonProgramColumns, offersActionColumn]
      : [...commonProgramColumns, ...accessPointExtraColumns];

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          {basePath === "/offers" && (
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
              Offer Point
            </p>
          )}
          <h1 className="text-2xl font-bold text-gray-800">Loyalty Programs</h1>
          <p className="text-gray-500 mt-1">
            Manage your loyalty programs and their attributes
          </p>
        </div>
        <div className="flex gap-3">
          {/* <PermissionGuard permissionCode={PERMISSION_CODES.CREATE_PROGRAM}>
            <Button
              variant="outline"
              className="bg-white border-gray-200"
              onClick={() => setClientSheetOpen(true)}
            >
              <Users className="h-4 w-4 mr-2" /> Add Client
            </Button>
          </PermissionGuard> */}
          {basePath === "/offers" ? (
            <Button className={primaryBtnClass} asChild>
              <Link to={`${basePath}/programs/create`}>
                <Plus className="h-4 w-4 mr-2" /> Add Program
              </Link>
            </Button>
          ) : (
            <PermissionGuard permissionCode={PERMISSION_CODES.CREATE_PROGRAM}>
              <Button className={primaryBtnClass} asChild>
                <Link to={`${basePath}/programs/create`}>
                  <Plus className="h-4 w-4 mr-2" /> Add Program
                </Link>
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {/* Filter section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1 flex gap-2 items-end">
          <div>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="programName">Program Name</SelectItem>
                <SelectItem value="programCode">Program Code</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder={`Search by ${
                  searchType === "programName" ? "program name" : "program code"
                }...`}
                className="pl-9"
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  setPage(0);
                  debouncedSearch(query);
                }}
              />
            </div>
          </div>
        </div>

        <div className="w-full sm:w-auto">
          <Select
            value={dateRange}
            onValueChange={(value) => {
              setDateRange(value);
              setPage(0); // Reset to first page when date range changes
            }}
            disabled={isLoading}
          >
            <SelectTrigger id="dateRange" className="w-full sm:w-[180px]">
              <SelectValue placeholder="Select a time period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="flex items-center gap-2 px-3 py-2 text-sm"
          onClick={resetFilters}
          disabled={isLoading}
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>

      <DataTable
        title="All Programs"
        data={isLoading ? [] : filteredPrograms}
        columns={programColumns}
        customTableBody={isLoading ? <ProgramTableSkeleton /> : undefined}
      />
      {!isLoading && totalPages ? (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={rowsPerPage.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <EnhancedPaginationControl
            totalPages={totalPages}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
          />
          <div className="w-[160px]" /> {/* Spacer to balance layout */}
        </div>
      ) : null}
      {/* Client Sheet */}
      <Sheet open={clientSheetOpen} onOpenChange={setClientSheetOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Add New Client</SheetTitle>
            <SheetDescription>
              Create a new client to associate with loyalty programs
            </SheetDescription>
          </SheetHeader>
          <ClientForm onSuccess={handleClientAdded} />
        </SheetContent>
      </Sheet>
      {/* Delete Program Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the program "
              {programToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <LoaderButton
              loading={isDeleting}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteProgram}
            >
              Delete
            </LoaderButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Programs;
